<template>
    <div class="dst search-sidebar-filters">
        <div>
            <div
                class="search-sidebar-filters__clear-filters-inner tw-border-b tw-border-solid tw-border-b-border-color"
            >
                <button
                    class="tw-font-bold tw-text-black tw-my-4 tw-py-4 tw-cursor-pointer"
                    :disabled="!hasSelectedFilters"
                    @click="onClearFiltersClick"
                >
                    <!-- @slot Left side of clear filters section -->
                    <slot name="clear-filters-left">
                        <div class="icon-text">
                            <span>{{ clearFiltersText }}</span>
                            <icon name="generic-delete" size="xs" />
                        </div>
                    </slot>
                </button>
                <div class="search-sidebar-filters__clear-filters-right">
                    <!-- @slot Right side of clear filters section -->
                    <slot name="clear-filters-right"></slot>
                </div>
            </div>
        </div>
        <div
            v-for="(filter, index) in filters"
            :key="`${filter.id}-${index}`"
            :class="[
                'search-sidebar-filters__group',
                `search-sidebar-filters__group--${filter.type}`,
                `search-sidebar-filters__group--id_${filter.id}`,
            ]"
            @click="trackFilterToggler(filter.title)"
        >
            <accordion
                v-if="filter.items && filter.items.length"
                :expanded="!filter.collapsed"
                :is-text="false"
                :no-transition="true"
                :no-hover="true"
                :title="filter.title"
                class="search-sidebar-filters__accordion"
            >
                <template v-if="isFilterType('group', filter)">
                    <div v-for="filterItem in getFilterItems(filter)" :key="filterItem.id">
                        <template v-if="isFilterType('select', filterItem)">
                            <div class="search-sidebar-filters__select">
                                <div
                                    class="ts--caption color--grey-900 ts-weight--700 m-bottom--m"
                                    v-html="filterItem.title"
                                ></div>
                                <div class="p-bottom--xs">
                                    <select
                                        :id="filterItem.id"
                                        :name="filterItem.name"
                                        :disabled="filterItem.options.length < 2"
                                        class="tw-w-full"
                                        @change="onSelectChange"
                                    >
                                        <option
                                            v-for="optionItem in filterItem.options"
                                            :key="optionItem.id"
                                            :value="optionItem.href"
                                            :selected="optionItem.state"
                                        >
                                            {{ optionItem.name }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                        </template>
                        <template v-if="isFilterType('input', filterItem)">
                            <div class="search-sidebar-filters__input">
                                <div
                                    class="ts--caption color--grey-900 ts-weight--700 m-bottom--m"
                                    v-html="filterItem.title"
                                ></div>
                                <div class="tw-flex items-center tw-w-full">
                                    <div class="tw-relative tw-border tw-w-full">
                                        <input
                                            autocomplete="false"
                                            aria-required="true"
                                            class="search-sidebar-filters__input-control tw-w-full tw-pr-4 tw-flex tw-items-center"
                                            :type="filterItem.inputType || 'text'"
                                            :name="filterItem.name"
                                            :placeholder="filterItem.placeholder"
                                            :value="filterItem.value"
                                            @keyup.enter="onInputValueChange"
                                            @focus="setInputSelection"
                                            @input="onInputValueInsert"
                                            @keypress="onKeyPress"
                                        >
                                        <div
                                            :class="{
                                                'search-sidebar-filters__input-icon tw-pr-4 tw-cursor-pointer': true,
                                                'search-sidebar-filters__input-icon--active tw-h-full tw-flex tw-justify-center tw-items-center':
                                                    filterItem.value,
                                            }"
                                            @click="onInputValueClear"
                                        >
                                            <icon name="generic-close" size="xs" />
                                        </div>
                                    </div>
                                    <button
                                        class="search-sidebar-filters__input-cta ts--caption tw-ml-4 tw-decoration-1 tw-underline"
                                        @click="onInputValueChange"
                                    >
                                        {{ $t('search-page.filters.apply') }}
                                    </button>
                                </div>
                                <div
                                    class="search-sidebar-filters__input-msg ts--caption tw-mt-2 tw-text-feedback-danger"
                                ></div>
                            </div>
                        </template>
                        <template v-if="isFilterType('slider', filterItem)">
                            <div class="search-sidebar-filters__slider">
                                <div
                                    class="ts--caption color--grey-900 ts-weight--700 m-bottom--m"
                                    v-html="filterItem.title"
                                ></div>
                                <div class="tw-mb-6">
                                    <dk-range-slider
                                        :id="filterItem.id"
                                        :nouislider-opts="filterItem.options"
                                        :format="filterItem.format"
                                        :editable="true"
                                        @set="onSliderUpdate"
                                    />
                                </div>
                                <div
                                    v-if="filterItem.description"
                                    class="search-sidebar-filters__group-item-description m-top--xs ts--caption color--grey-700"
                                    v-html="filterItem.description"
                                ></div>
                            </div>
                        </template>
                        <template v-else-if="isFilterType('checkbox', filterItem)">
                            <list-group
                                :items="mappedCheckboxesItems(filterItem)"
                                variant="trailing-checkbox"
                                @change="onCheckboxClick"
                            />
                        </template>
                        <template v-else-if="isFilterType('switcher', filterItem)">
                            <div class="search-sidebar-filters__switcher">
                                <dk-switch
                                    v-model="filterItem.state"
                                    :checked="filterItem.state"
                                    size="small"
                                    @change="togglePromotions(filterItem)"
                                >
                                    <span class="ts--body-small color--grey-900">
                                        {{ filterItem.name }}
                                    </span>
                                </dk-switch>
                            </div>
                        </template>
                    </div>
                    <div v-if="filter.isExpandable">
                        <a
                            href="#0"
                            class="ts-link ts-link--primary d--block p-top--s p-bottom--s p-left--m p-right--m"
                            @click.prevent="handleItemsToggler(filter, $event)"
                        >
                            <span class="ts--body-small">{{
                                $t(`${!filter.isExpanded ? 'search-page.show-more' : 'search-page.show-less'}`)
                            }}</span>
                        </a>
                    </div>
                </template>
                <template v-if="isFilterType('chip', filter)">
                    <div class="search-sidebar-filters__chips">
                        <dk-chips v-model="filter.items" variant="outlined" :max-items="3" @click="onChipClick" />
                    </div>
                </template>
            </accordion>
        </div>
    </div>
</template>

<script>
import Accordion from '@v4/common/components/molecules/AccordionDST.vue';
import gaEventHandler from '@v4/common/components/mixins/google-analytics';
import ListGroup from '@v4/common/components/organisms/ListGroup';
import Icon from '@v4/common/components/atoms/Icon';
import DkChips from '@v4/common/components/molecules/DkChips.vue';
import { EventBus } from '@v4/common/utils/event-bus';
import generateChip from '@v4/search/components/organisms/chip-generator';
import { URL_STOCK } from '../../config';

export default {
    name: 'SearchSidebarFilters',
    components: {
        Accordion,
        DkRangeSlider: () => ({
            component: import(
                /* webpackChunkName: "DkRangeSlider-vue" */ '@v4/common/components/molecules/DkRangeSliderDST.vue'
            ),
        }),
        DkSwitch: () => ({
            component: import(/* webpackChunkName: "DkSwitch-vue" */ '@v4/common/components/atoms/DkSwitch.vue'),
        }),
        ListGroup,
        Icon,
        DkChips,
    },
    mixins: [gaEventHandler],
    props: {
        filterList: {
            type: Array,
            default: () => [],
        },
        clearFiltersText: {
            type: String,
            default() {
                return this.$t('search-page.button.clear-filters');
            },
        },
    },
    data() {
        return {
            /**
             * In case of zipcode error from API we don't get any response,
             * so we need to 'manually' create the chip with this wrong zipcode
             * to enable the user to delete this filter.
             */
            invalidZipCode: '',
            invalidZipCodeChip: undefined,
            previousZipCode: '',
        };
    },
    computed: {
        // cloning filters list to avoid Vue error about props mutation
        // when adding a manually generated chip to the filters array
        finalFilterList() {
            return this.filterList;
        },
        hasSelectedFilters() {
            return !!this.finalFilterList.filter(
                (group) =>
                    group.id !== 'sort' &&
                    (group.active ||
                        (group.items &&
                            group.items.length &&
                            group.items.filter((item) => item.selected || item.state || item.active).length))
            ).length;
        },
        filters() {
            // keep only the filters that have `id` and `type` properties
            return this.finalFilterList.filter((filter) => filter.id && filter.type);
        },
    },
    mounted() {
        EventBus.$on('SearchPage:addInvalidZipCodeChip', this.handleInvalidZipCode);

        // in order to handle correctly the zipcode chips in case when the user insert an invalid zipcode
        // AFTER a valid one, we need to force the deletion of the chip with a valid zipcode
        EventBus.$on('SearchPage:zipCodeChange', this.registerPreviousZipCode);
    },
    beforeDestroy() {
        EventBus.$off('SearchPage:addInvalidZipCodeChip', this.handleInvalidZipCode);
        EventBus.$off('SearchPage:zipCodeChange', this.registerPreviousZipCode);
    },
    methods: {
        onSliderUpdate(slider, el) {
            this.$emit('range-slider-update', slider, el);
        },
        onCheckboxClick({ event }) {
            this.$emit('checkbox-click', event);
        },
        togglePromotions(item) {
            this.$emit('promotion-switcher-change', item);
        },
        onClearFiltersClick(e) {
            if (this.hasSelectedFilters) {
                this.$emit('clear-filters-click', e);
            }
        },
        onChipClick(...args) {
            /**
             * Fired when chip item has been clicked
             *
             * @event chip-click
             * @type {object}
             */

            const { item } = args[0];
            if (item.label === this.invalidZipCode) {
                this.deleteInvalidZipCodeChip();
            }

            this.$emit('chip-click', ...args);
        },
        onSelectChange(...args) {
            /**
             * Fired when select box has been changed
             *
             * @event select-change
             * @type {object}
             */
            this.$emit('select-change', ...args);
        },
        onInputValueChange(...args) {
            const event = args[0];
            event.target.blur();

            /**
             * Fired when input value has been changed
             *
             * @event input-value-change
             * @type {object}
             */
            this.$emit('input-value-change', ...args);
        },
        onInputValueClear(...args) {
            /**
             * Fired when input value has been cleared
             *
             * @event input-value-clear
             * @type {object}
             */
            this.$emit('input-value-clear', ...args);
            if (this.invalidZipCode) {
                this.deleteInvalidZipCodeChip();
            }

            if (this.previousZipCode) {
                this.previousZipCode = '';
            }
        },
        onInputValueInsert(...args) {
            /**
             * Fired when input value has been inserted
             *
             * @event key-press
             * @type {object}
             */
            this.$emit('input-value-insert', ...args);
        },
        onKeyPress(...args) {
            /**
             * Fired when input value has been inserted
             *
             * @event key-press
             * @type {object}
             */
            this.$emit('key-press', ...args);
        },
        setInputSelection(e) {
            e.target.select();
        },
        isFilterType(name, filter) {
            return filter && filter.type === name;
        },
        trackFilterToggler(filterTitle) {
            this.pushGaEvent('Search page', 'accordion filter open/close', `${filterTitle} filter`);
        },
        getFilterItems(filter) {
            const items = (filter && filter.items) || [];

            // Let's sort items based on `collapsedOrder` property
            // which contains a desired number order when the fitler is exapandable
            // and not expanded yet.
            if (filter.isExpandable && !filter.isExpanded) {
                const filteredItems = items
                    .filter((item) => item.collapsedOrder)
                    .sort((firstItem, secondItem) => firstItem.collapsedOrder - secondItem.collapsedOrder);

                if (filteredItems.length) {
                    return filteredItems;
                }

                // When for some reason we don't have filtered items as fallback
                // let's show NOT filtered items, in order to not show an empty list.
                filter.isExpandable = false;
            }

            return items;
        },
        mappedCheckboxesItems(item) {
            return [
                {
                    label: item.name,
                    checked: item.state,
                    name: item.name,
                    value: item.href,
                    description: item.description,
                    iconName: item.iconName,
                },
            ];
        },
        handleItemsToggler(filter, event) {
            filter.isExpanded = !filter.isExpanded;

            /**
             * Fired when items toggler has been clicked
             *
             * @event items-toggler-click
             * @type {object}
             * @property {object} filter - Filter object item
             * @property {Event} event - Click event
             * @property {boolean} expanded - Toggled or not
             */
            this.$emit('items-toggler-click', { filter, event, expanded: filter.isExpanded });
        },
        handleInvalidZipCode(zipCode) {
            if (this.previousZipCode) {
                this.forcePreviousZipCodeChipDeletion();
            }

            this.createInvalidZipCodeChip(zipCode);
            this.updateZipCodeInputData();
        },
        createInvalidZipCodeChip(zipCode) {
            this.invalidZipCode = zipCode;
            this.invalidZipCodeChip = generateChip(zipCode, URL_STOCK);
            this.finalFilterList.unshift(this.invalidZipCodeChip);
        },
        // method needed to keep the locality accordion expanded
        // to enable the user easily clear the input with invalid zipcode
        updateZipCodeInputData() {
            this.finalFilterList.map((filterObject) => {
                if (filterObject.id === 'locality') {
                    filterObject.items.forEach((filter) => {
                        if (filter.type === 'input') {
                            filter.value = this.invalidZipCode;
                            filterObject.collapsed = false;
                        }
                    });
                }
            });
        },
        deleteInvalidZipCodeChip() {
            this.invalidZipCodeChip = undefined;
            this.invalidZipCode = '';
            this.previousZipCode = '';
        },
        registerPreviousZipCode(zipCode) {
            this.previousZipCode = zipCode;
        },
        forcePreviousZipCodeChipDeletion() {
            this.finalFilterList.map((filterObject) => {
                const { type, items } = filterObject;
                if (type === 'chip' && items[0].label === this.previousZipCode) {
                    const index = this.finalFilterList.indexOf(filterObject);
                    this.finalFilterList.splice(index, 1);
                }
            });

            this.previousZipCode = '';
        },
    },
};
</script>
